import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, CreditCard } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface ServiceCard {
  id: string;
  patient_id: string;
  card_number: string;
  total_sessions: number;
  used_sessions: number;
  purchase_date: string;
  expiry_date: string;
  status: string;
  patients?: { full_name: string };
  treatments?: { name: string };
}

const ServiceCards = () => {
  const [cards, setCards] = useState<ServiceCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const { data, error } = await supabase
        .from('service_cards')
        .select('*, patients(full_name), treatments(name)')
        .order('purchase_date', { ascending: false });

      if (error) throw error;
      setCards(data || []);
    } catch (error) {
      console.error('Error fetching service cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCards = cards.filter(card =>
    card.card_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.patients?.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getProgressPercentage = (used: number, total: number) => {
    return (used / total) * 100;
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <CreditCard className="text-blue-600" />
              Service Cards
            </h2>
            <p className="text-gray-600 mt-1">Manage patient service cards and packages</p>
          </div>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
            <Plus size={20} />
            New Service Card
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search service cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCards.map((card) => {
              const progress = getProgressPercentage(card.used_sessions, card.total_sessions);
              const remaining = card.total_sessions - card.used_sessions;

              return (
                <div key={card.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">{card.card_number}</h3>
                      <p className="text-sm text-gray-600 mt-1">{card.patients?.full_name}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(card.status)}`}>
                      {card.status}
                    </span>
                  </div>

                  {card.treatments?.name && (
                    <p className="text-sm text-gray-700 mb-3">{card.treatments.name}</p>
                  )}

                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Sessions Used</span>
                      <span className="font-medium text-gray-900">
                        {card.used_sessions} / {card.total_sessions}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{remaining} sessions remaining</p>
                  </div>

                  <div className="text-xs text-gray-500 mb-3">
                    <div>Purchased: {new Date(card.purchase_date).toLocaleDateString()}</div>
                    {card.expiry_date && (
                      <div>Expires: {new Date(card.expiry_date).toLocaleDateString()}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t">
                    <button className="flex-1 text-center text-blue-600 hover:text-blue-800 text-sm py-2">
                      <Edit size={16} className="inline" />
                    </button>
                    <button className="flex-1 text-center text-red-600 hover:text-red-800 text-sm py-2">
                      <Trash2 size={16} className="inline" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceCards;
